/*!
 * aos-lite.js
 *
 * Drop-in replacement for the AOS 2.3.4 scroll-animation library that ships
 * inside vendor-scripts-v11.js. AOS 2.3.4 carries its own Object.assign
 * polyfill and old-IE detection code (Lighthouse's "Legacy JavaScript"
 * flag) and does its viewport-position math on scroll/resize listeners
 * plus a MutationObserver, which forces synchronous layout reads
 * (Lighthouse's "Forced reflow" flag).
 *
 * This file exposes the exact same window.AOS.init / .refresh / .refreshHard
 * surface the theme already calls (see theme.js), but implements it with
 * IntersectionObserver, so:
 *   - every existing AOS.init(...) / AOS.refresh() / AOS.refreshHard() call
 *     site in theme.js keeps working with zero changes there
 *   - the old AOS runtime (polyfills, scroll listeners, MutationObserver)
 *     never executes, because this file overwrites window.AOS before
 *     theme.js calls AOS.init()
 *   - the theme's existing CSS (.aos-animate[data-aos=...] rules already in
 *     theme.css) is untouched and keeps working unmodified, since this
 *     script's only job is toggling the same "aos-animate" class AOS used
 *
 * Load order requirement: this file must be included AFTER
 * vendor-scripts-v11.js (which defines the real AOS) and BEFORE theme.js
 * (which calls AOS.init()) — both as defer scripts, so document order
 * controls execution order.
 *
 * Note: this does not shrink vendor-scripts-v11.js itself. That file is a
 * single minified bundle of several vendored libraries (AOS, Flickity,
 * PhotoSwipe, noUiSlider, JS-Cookie) with no clean byte boundaries between
 * them, so safely removing AOS's bytes requires rebuilding that bundle from
 * source with the theme's original build tooling, not hand-editing the
 * minified output. This file only removes AOS from ever *running*.
 */
(function () {
  'use strict';

  var observer = null;
  var settings = {
    offset: 120,
    once: false,
  };
  var observed = new WeakSet();

  function elementOffset(el) {
    var raw = el.getAttribute('data-aos-offset');
    var n = raw !== null ? parseInt(raw, 10) : NaN;
    return isNaN(n) ? settings.offset : n;
  }

  function makeObserver(offset) {
    // Positive AOS "offset" means "trigger this many px before the element
    // would otherwise enter the viewport" — approximate with a negative
    // bottom rootMargin so IntersectionObserver fires that much earlier.
    return new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var el = entry.target;
          if (entry.isIntersecting) {
            el.classList.add('aos-animate');
            if (settings.once) {
              entry.target.__aosObserver && entry.target.__aosObserver.unobserve(el);
              observed.delete(el);
            }
          } else if (!settings.once) {
            el.classList.remove('aos-animate');
          }
        });
      },
      { root: null, rootMargin: '0px 0px -' + Math.max(offset, 0) + 'px 0px', threshold: 0 }
    );
  }

  // Group elements by their effective offset so each group gets an
  // IntersectionObserver tuned to the right rootMargin (mirrors AOS
  // supporting a per-element data-aos-offset override).
  var observersByOffset = {};

  function observerFor(offset) {
    if (!observersByOffset[offset]) {
      observersByOffset[offset] = makeObserver(offset);
    }
    return observersByOffset[offset];
  }

  function observeAll() {
    var els = document.querySelectorAll('[data-aos]');
    els.forEach(function (el) {
      if (observed.has(el)) return;
      var offset = elementOffset(el);
      var obs = observerFor(offset);
      el.__aosObserver = obs;
      obs.observe(el);
      observed.add(el);
    });
  }

  window.AOS = {
    init: function (opts) {
      opts = opts || {};
      if (typeof opts.offset === 'number') settings.offset = opts.offset;
      settings.once = !!opts.once;
      // easing / duration / disableMutationObserver are no-ops here: this
      // theme's animations are pure CSS (see .aos-animate[data-aos=...]
      // rules in theme.css), and we deliberately don't use a
      // MutationObserver — that's the point of this file.
      observeAll();
    },
    refresh: function () {
      observeAll();
    },
    refreshHard: function () {
      observeAll();
    },
  };
})();
